/**
 * Oath Electronic Power Service
 *
 * Fetches the electronic-power ranges assigned to an agency (and its
 * sub-agencies) so the portal can badge power numbers that fall inside
 * one of those ranges as "eBond".
 *
 * Backend contract: GET /oath/powerinventory/{agencyId}/electronic-ranges
 * returns a BARE array (no BaseResponse envelope) — that's the Oath
 * convention, verified with the backend team. Do NOT try to read
 * `.result` off the response.
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: { ...getAuthHeaders(), ...options.headers },
      });
    }
  }

  return response;
};

/**
 * Fetch electronic ranges for an agency + its sub-agencies.
 *
 * @param {number|string} agencyId
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 *   Success `data` is an array of:
 *     {
 *       beginningPowerRange: string,
 *       endingPowerRange:    string,
 *       powerAmountCd:       number,
 *       powerAmountDescription: string,
 *       powerExpirationDate: string,   // full ISO datetime
 *       agentId:             number,
 *     }
 */
export const getElectronicRanges = async (agencyId) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/powerinventory/${agencyId}/electronic-ranges`
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || `Error ${response.status}` };
    }
    // Bare array on success — no envelope. Do not touch .result here.
    const data = await response.json().catch(() => []);
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
